import type { Account } from '@api/hooks/useAccounts';
import { usePaginatedAccounts } from '@hooks/usePaginatedAccounts';
import { forwardRef } from 'react';
import { useEffect, useState } from 'react';
import type { ControllerRenderProps, Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { AccountList } from './AccountList';

type ControlledAccountListProps<T> = {
	name: Path<T>;
	label?: string;
	disabledAccounts?: string[];
	setDefaultValue?: (value: string) => void;
	defaultValue?: Account;
	isDisabled?: boolean;
};

export const ControlledAccountList = <T,>({
	name,
	label,
	setDefaultValue,
	disabledAccounts = [],
	defaultValue,
	isDisabled,
}: ControlledAccountListProps<T>) => {
	const { control } = useFormContext();

	return (
		<Controller
			control={control}
			name={name as Path<T>}
			render={({ field: { ref, ...field } }) => (
				<AccountListWrapper
					ref={ref}
					defaultValue={defaultValue}
					disabledAccounts={disabledAccounts}
					field={field}
					isDisabled={isDisabled}
					label={label}
					setDefaultValue={setDefaultValue}
				/>
			)}
		/>
	);
};

interface AccountListWrapperProps {
	field: Omit<ControllerRenderProps, 'ref'>;
	label?: string;
	disabledAccounts?: string[];
	setDefaultValue?: (value: string) => void;
	defaultValue?: Account;
	isDisabled?: boolean;
}

const AccountListWrapper = forwardRef<HTMLDivElement, AccountListWrapperProps>(
	(
		{ field, label, disabledAccounts = [], setDefaultValue, defaultValue, isDisabled },
		ref,
	) => {
		const [value, setValue] = useState<Account | undefined>(defaultValue);
		const { accounts, isLoading, pagination } = usePaginatedAccounts();

		const setFirstAsDefault = (account: Account) => {
			setValue(account);
			setDefaultValue?.(account.uuid);
		};

		const handleChange = (account: Account) => {
			setValue(account);
			field.onChange(account.uuid);
		};

		useEffect(() => {
			if (!field.value && accounts.length > 0 && !!setDefaultValue) {
				const firstAccount = accounts[0];
				setFirstAsDefault(firstAccount);
			}
		}, [field.value, accounts.length, field.onChange, accounts, setDefaultValue]);

		return (
			<div ref={ref} className="mt-space-40">
				<AccountList
					{...field}
					disabledAccounts={disabledAccounts}
					isDisabled={isDisabled}
					isLoading={isLoading || !value}
					label={label}
					onChange={handleChange}
					options={accounts}
					pagination={pagination}
					value={value}
				/>
			</div>
		);
	},
);

AccountListWrapper.displayName = 'AccountListWrapper';
